import type { TPAPublicClientSpecEntry } from '@wix/thunderbolt-ssr-api'
import type { SiteConfigFn } from '@wix/thunderbolt-vsm-api-types'
import type { OOISiteConfig, OOIComponentData, FormFactor, ViewMode } from '../types'

const ooiSiteConfig: SiteConfigFn<OOISiteConfig> = ({ api, funcLib }) => {
	// Get appsUrlData from platform
	const appsUrlData = api.platform.getAppsUrlData()

	// Get all widgets on site from renderHints
	const applications = api.rendererModel.getSiteMetaData()?.renderHints?.applications ?? {}
	const allWidgetsOnSite: string[] = []
	for (const application of Object.values(applications)) {
		const widgetsCount = application.widgetsCount ?? {}
		allWidgetsOnSite.push(...Object.keys(widgetsCount))
	}

	// Get query params
	const url = api.originalRequest.getUrl() ?? ''
	const cssPerBreakpointParam = funcLib.getQueryParamValue(url, 'cssPerBreakpoint')
	const loadStaticCssWithLinkParam = funcLib.getQueryParamValue(url, 'loadStaticCssWithLink')

	// Get isEditor/isStudio/isBuilderComponentModel (for editor context, default to false for site)
	const isEditor = false
	const isStudio = false
	const isBuilderComponentModel = api.rendererModel.getIsBuilderComponentModel()

	// Get clientSpecMap for widgetToConfig
	const clientSpecMap = api.rendererModel.getClientSpecMap()

	// Build widgetToConfig map - keyed by widgetId (matching Carmi behavior)
	const widgetToConfig: Record<string, any> = {}
	for (const csm of Object.values(clientSpecMap)) {
		// Check if this entry has widgets (no type check, matching Carmi)
		const widgets = (csm as any).widgets
		if (widgets) {
			for (const widget of Object.values(widgets)) {
				const widgetId = (widget as any).widgetId
				const componentFields = (widget as any).componentFields || {}

				// Match Carmi behavior: use false as default instead of undefined
				widgetToConfig[widgetId] = {
					isLoadable: componentFields.isLoadable ?? false,
					isServerBundled: componentFields.isServerBundled ?? false,
					loadStaticCssWithLink:
						componentFields.cssPerBreakpoint ?? (loadStaticCssWithLinkParam === 'true' ? true : false),
					isModuleFederated: componentFields.isModuleFederatedWidget ?? false,
				}
			}
		}
	}

	// Helper to resolve noCssComponentUrl
	const resolveNoCssComponentUrl = (widget: any): string => {
		const widgetId = widget.widgetId
		const isWidgetOnSite = allWidgetsOnSite.includes(widgetId)
		const cssPerBreakpoint = widget.componentFields?.cssPerBreakpoint ?? false

		if (
			isWidgetOnSite ||
			cssPerBreakpointParam === 'true' ||
			(isEditor && (isStudio || isBuilderComponentModel) && cssPerBreakpoint)
		) {
			return widget?.noCssComponentUrl as string
		}
		return ''
	}

	// Build ooiComponentsData from appsUrlData
	const ooiComponentsData: Record<string, OOIComponentData> = {}

	for (const [appDefId, app] of Object.entries(appsUrlData)) {
		const widgets = app.widgets ?? {}
		const staticsBaseUrl = app.baseUrls?.staticsBaseUrl

		for (const [widgetKey, widget] of Object.entries(widgets)) {
			const componentUrl = widget.componentUrl
			if (!componentUrl) {
				continue // Filter out widgets without componentUrl
			}

			const widgetId = widget.widgetId
			// Look up config by widgetId (matching Carmi behavior)
			const widgetConfig = widgetToConfig[widgetId] ?? {
				isLoadable: false,
				isServerBundled: false,
				loadStaticCssWithLink: false,
				isModuleFederated: false,
			}

			ooiComponentsData[widgetKey] = {
				sentryDsn: widget.errorReportingUrl as string,
				componentUrl,
				widgetId,
				noCssComponentUrl: resolveNoCssComponentUrl(widget),
				staticBaseUrl: (widget as any).staticsBaseUrl || staticsBaseUrl,
				isLoadable: widgetConfig.isLoadable,
				isServerBundled: widgetConfig.isServerBundled,
				loadStaticCssWithLink: widgetConfig.loadStaticCssWithLink,
				isModuleFederated: widgetConfig.isModuleFederated,
			}
		}
	}

	const shouldLoadSiteFeature = Object.keys(ooiComponentsData).length > 0

	if (!shouldLoadSiteFeature) {
		return {
			shouldLoadSiteFeature: false,
			shouldLoadSiteConfig: false,
			siteConfig: null,
		}
	}

	// Get form factor and capitalize it
	const formFactor = api.env.getFormFactor()
	const formFactorCapitalized = (formFactor.charAt(0).toUpperCase() + formFactor.slice(1)) as FormFactor

	// Get blog mobile component URL
	const blogAppBaseUrl = appsUrlData['14bcded7-0066-7c35-14d7-466cb3f09103']?.baseUrls?.staticsBaseUrl
	const blogMobileComponentUrl = `${blogAppBaseUrl}feed-page-mobile-viewer.bundle.min.js`

	const siteConfig: OOISiteConfig = {
		ooiComponentsData,
		viewMode: 'Site' as ViewMode,
		formFactor: formFactorCapitalized,
		blogMobileComponentUrl,
		isBuilderComponentModel: isBuilderComponentModel as boolean,
	}

	return {
		shouldLoadSiteFeature: true,
		shouldLoadSiteConfig: true,
		siteConfig,
	}
}

const name = 'ooi'

export const config = { site: ooiSiteConfig, name }
